import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { createLink } from "apollo-v3-absinthe-upload-link";

import { LanguagesEnum } from "globalConstants/languagesEnum";
import { EXTERNAL_URI } from "settings";
import type { TGraphQLError } from "settings/apollo/types";
import { createErrorReportingLink, createSentryBreadcrumbLink } from "settings/apolloErrorLink";
import i18n from "translations/i18n";

import { ExternalSessionLossEnum, externalSessionLossVar, getExternalSessionLoss } from "./session";

export const externalApolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  defaultOptions: { watchQuery: { fetchPolicy: "cache-and-network" }, query: { fetchPolicy: "network-only" } },
});

const uploadLink = createLink({ uri: EXTERNAL_URI, credentials: "include", fetch });

const localeLink = setContext((_operation, { headers = {} as Record<string, string> }) => ({
  headers: { ...headers, locale: i18n.language || LanguagesEnum.brazilianPortuguese },
}));

const sessionLink = onError(({ operation, graphQLErrors }) => {
  const mainDefinition = getMainDefinition(operation.query);
  const isQuery = mainDefinition.kind === "OperationDefinition" && mainDefinition.operation === "query";

  (graphQLErrors as readonly TGraphQLError[] | undefined)?.forEach(error => {
    const sessionLoss = getExternalSessionLoss(error, isQuery);

    if (sessionLoss) {
      externalSessionLossVar(sessionLoss);
    } else if (!isQuery && error.code === "NOT_FOUND") {
      externalApolloClient.refetchQueries({ include: "active" });
    }
  });
});

externalApolloClient.setLink(
  ApolloLink.from([
    createErrorReportingLink({
      silentCodes: [ExternalSessionLossEnum.Unauthorized],
      unreportedCodes: ["OTP_COOLDOWN"],
    }),
    sessionLink,
    createSentryBreadcrumbLink(EXTERNAL_URI),
    localeLink,
    uploadLink,
  ]),
);
